import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowUp, RotateCcw, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

type ChatMessage = {
  role: "user" | "assistant";
  content: string;
};

export interface ApuriContext {
  page: string;
  isSignedIn: boolean;
  plan?: string | null;
  subscriptionActive?: boolean;
  credits?: number;
  userName?: string | null;
  tripCount?: number;
  reportCount?: number;
  hasOrganization?: boolean;
  orgName?: string | null;
  orgRole?: string | null;
  orgPlan?: string | null;
  orgCredits?: number;
  orgSeatLimit?: number;
  orgMemberCount?: number;
}

interface HelpChatViewProps {
  context: ApuriContext;
  quickQuestions: string[];
}

function parseSSE(chunk: string, buffer: string): { text: string; remaining: string } {
  const combined = buffer + chunk;
  const lines = combined.split("\n");
  let text = "";
  let remaining = "";

  if (!combined.endsWith("\n")) {
    remaining = lines.pop() ?? "";
  }

  for (const line of lines) {
    if (!line.startsWith("data: ")) continue;
    const data = line.slice(6).trim();
    if (data === "[DONE]") continue;
    try {
      const parsed = JSON.parse(data);
      if (parsed.type === "content_block_delta" && parsed.delta?.text) {
        text += parsed.delta.text;
      }
    } catch {
      // Skip unparseable lines
    }
  }

  return { text, remaining };
}

export default function HelpChatView({ context, quickQuestions }: HelpChatViewProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    const textarea = inputRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  }, [input]);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isStreaming) return;

      setInput("");
      setError(null);

      const userMessage: ChatMessage = { role: "user", content: trimmed };
      const nextMessages = [...messages, userMessage];
      setMessages(nextMessages);

      setIsStreaming(true);
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const { data: { session } } = await supabase.auth.getSession();

        const res = await fetch(
          `${String(import.meta.env.VITE_SUPABASE_URL).trim()}/functions/v1/apuri-chat`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(session?.access_token
                ? { Authorization: `Bearer ${session.access_token}` }
                : {}),
            },
            body: JSON.stringify({
              messages: nextMessages.slice(-20),
              context,
            }),
            signal: controller.signal,
          }
        );

        if (!res.ok) {
          const errBody = await res.json().catch(() => null);
          throw new Error(errBody?.error ?? `Virhe (${res.status})`);
        }

        if (!res.body) throw new Error("Ei vastausta");

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let assistantText = "";
        let sseBuffer = "";

        setMessages((prev) => [...prev, { role: "assistant", content: "" }]);

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const { text: newText, remaining } = parseSSE(chunk, sseBuffer);
          sseBuffer = remaining;

          if (newText) {
            assistantText += newText;
            const captured = assistantText;
            setMessages((prev) => {
              const updated = [...prev];
              updated[updated.length - 1] = { role: "assistant", content: captured };
              return updated;
            });
            scrollToBottom();
          }
        }

        if (sseBuffer) {
          const { text: finalText } = parseSSE(sseBuffer + "\n", "");
          if (finalText) {
            assistantText += finalText;
            const captured = assistantText;
            setMessages((prev) => {
              const updated = [...prev];
              updated[updated.length - 1] = { role: "assistant", content: captured };
              return updated;
            });
          }
        }

        if (!assistantText) {
          setMessages((prev) => prev.slice(0, -1));
          setError("Apuri ei vastannut. Yritä uudelleen.");
        }
      } catch (err) {
        if ((err as Error).name === "AbortError") return;
        console.error("[apuri-chat]", err);
        setMessages((prev) =>
          prev.length > 0 && prev[prev.length - 1].role === "assistant" && !prev[prev.length - 1].content
            ? prev.slice(0, -1)
            : prev
        );
        setError((err as Error).message || "Jokin meni pieleen. Yritä uudelleen.");
      } finally {
        setIsStreaming(false);
        abortRef.current = null;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isStreaming, messages, context]
  );

  const handleSubmit = useCallback(() => {
    sendMessage(input);
  }, [input, sendMessage]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  const resetChat = useCallback(() => {
    abortRef.current?.abort();
    setMessages([]);
    setInput("");
    setError(null);
    setIsStreaming(false);
  }, []);

  const isEmpty = messages.length === 0;

  return (
    <div className="flex h-full flex-col">
      {/* Messages area */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-1 py-2 space-y-3">
        {isEmpty && (
          <div className="space-y-3 pt-1">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              <span className="font-medium">
                {context.isSignedIn
                  ? `Hei${context.userName ? ` ${context.userName.split(" ")[0]}` : ""}! Miten voin auttaa?`
                  : "Miten voin auttaa?"}
              </span>
            </div>
            <div className="grid grid-cols-1 gap-1.5">
              {quickQuestions.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => sendMessage(q)}
                  className="rounded-xl border border-border/60 bg-background px-3 py-2 text-left text-sm transition-colors hover:bg-muted"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div
            key={i}
            className={cn(
              "flex",
              msg.role === "user" ? "justify-end" : "justify-start"
            )}
          >
            <div
              className={cn(
                "max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap",
                msg.role === "user"
                  ? "bg-primary text-primary-foreground rounded-br-md"
                  : "bg-muted/70 text-foreground rounded-bl-md"
              )}
            >
              {msg.content || (
                <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                  <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-primary/60" />
                  <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-primary/60 [animation-delay:150ms]" />
                  <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-primary/60 [animation-delay:300ms]" />
                </span>
              )}
            </div>
          </div>
        ))}

        {error && (
          <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            {error}
          </div>
        )}
      </div>

      {/* Input area */}
      <div className="shrink-0 border-t border-border/60 px-1 pt-2 pb-1">
        {messages.length > 0 && (
          <button
            type="button"
            onClick={resetChat}
            className="mb-2 inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <RotateCcw className="h-3 w-3" />
            Uusi keskustelu
          </button>
        )}
        <div className="relative">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Kysy mitä vain..."
            rows={1}
            disabled={isStreaming}
            className="w-full resize-none rounded-xl border border-border/60 bg-background py-2.5 pl-3.5 pr-10 text-sm placeholder:text-muted-foreground/60 focus:border-primary/40 focus:outline-none focus:ring-1 focus:ring-primary/20 disabled:opacity-50"
          />
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isStreaming || !input.trim()}
            className="absolute bottom-2 right-2 inline-flex h-7 w-7 items-center justify-center rounded-lg bg-primary text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-30 disabled:hover:bg-primary"
            aria-label="Lähetä"
          >
            <ArrowUp className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-1.5 text-center text-[10px] text-muted-foreground/50">
          Apuri voi tehdä virheitä. Tarkista tärkeät tiedot.
        </p>
      </div>
    </div>
  );
}
